/**
 * 데스크탑 셸과 웹뷰를 잇는 보이지 않는 부품(AppShell에 한 번 둔다).
 *
 * - 종료 요청 → 저장 flush → 닫기. flush가 실패하면 닫지 않고 묻는다(§4.4, ADV-C01).
 * - MCP 브리지 준비 신호, 메뉴 명령, 외부 링크(http/https만 시스템 브라우저, ADV-F06), 화면 오류·저장 실패 기록.
 * - 웹에서도 페이지를 떠날 때 대기 중인 저장을 바로 쓴다(`pagehide`).
 */
import { Body1 } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { AppDialog } from "@/components/app-dialog";
import { FuiButton } from "@/components/fui-control";
import { useSettingsHub } from "@/shell/settings-hub";
import { todayISO } from "@/engine/date";
import { catalogSnapshot } from "@/store/catalog-store";
import { flushPendingSaves, useProjectStore } from "@/store/project-store";
import { onSaveFailure } from "@/store/save-health";
import { clientLog } from "./client-log";
import { saveText } from "./file-io";
import { startMcpBridge } from "./mcp-bridge";
import { currentWindow, invoke, isDesktop, listen, platform } from "./runtime";
import { markDesktopDocument } from "./window-chrome";

function tryFlush(): boolean {
  try {
    flushPendingSaves();
    return true;
  } catch {
    return false;
  }
}

/** 저장이 막혔을 때 지금 편집 중인 플랜(없으면 전체 카탈로그)을 JSON으로 내보낸다. */
export async function exportRescueCopy(): Promise<boolean> {
  const s = useProjectStore.getState();
  if (s.activeId) return (await saveText(`${s.project.name || "플랜"}.json`, s.exportProject())) === "saved";
  return (await saveText(`xplanner-백업-${todayISO()}.json`, JSON.stringify(catalogSnapshot()))) === "saved";
}

function handleMenu(id: string) {
  const open = useProjectStore.getState().openDialog;
  if (id === "nav:logs") useSettingsHub.getState().open("app", "logs");
  else if (id === "nav:mcp") useSettingsHub.getState().open("app", "integration");
  else if (id === "open:settings") open("settings");
  else if (id === "open:shortcuts") open("shortcuts");
}

function interceptLinks(e: MouseEvent) {
  if (e.defaultPrevented || (e.type === "click" && e.button !== 0)) return;
  const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
  if (!a || a.hasAttribute("download")) return;
  let url: URL;
  try {
    url = new URL(a.getAttribute("href") ?? "", window.location.href);
  } catch {
    return;
  }
  if (url.origin === window.location.origin) return;
  e.preventDefault();
  if (url.protocol === "http:" || url.protocol === "https:") void invoke("open_external_url", { url: url.href }).catch(() => {});
}

export function DesktopRuntime() {
  const [closeBlocked, setCloseBlocked] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const onHide = () => void tryFlush();
    window.addEventListener("pagehide", onHide);
    markDesktopDocument();
    if (!isDesktop()) return () => window.removeEventListener("pagehide", onHide);

    document.title = "XPlanner";
    const root = document.documentElement;

    let disposed = false;
    const cleanups: Array<() => void> = [];
    const keep = (p: Promise<() => void>) =>
      void p
        .then((off) => (disposed ? off() : cleanups.push(off)))
        .catch((e: unknown) => console.warn("[desktop] 초기화 실패:", e instanceof Error ? e.message : String(e)));

    void startMcpBridge().catch((e: unknown) => console.warn("[desktop] MCP 브리지 준비 실패:", e instanceof Error ? e.message : String(e)));
    keep(listen<string>("menu:action", handleMenu));
    keep(
      listen<null>("app:close-requested", () => {
        if (tryFlush()) void invoke("app_close_confirmed");
        else setCloseBlocked(true);
      }),
    );

    document.addEventListener("click", interceptLinks, true);
    document.addEventListener("auxclick", interceptLinks, true);

    const onError = (ev: ErrorEvent) =>
      clientLog({ level: "error", type: "error", action: "webview_error", status: "fail", summary: String(ev.message || "화면 오류").slice(0, 200) });
    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason instanceof Error ? ev.reason.message : String(ev.reason ?? "");
      clientLog({ level: "error", type: "error", action: "webview_rejection", status: "fail", summary: reason.slice(0, 200) });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    const offSave = onSaveFailure((f) =>
      clientLog({ level: "error", type: "storage", action: "save_failed", status: "fail", summary: f.message, meta: { quota: f.quota } }),
    );

    // 전체 화면에서는 신호등이 숨으므로 창 맨 위 줄의 왼쪽 여백을 거둔다.
    const syncFullscreen = async () => {
      if (platform() !== "macos") return;
      const w = await currentWindow();
      const full = w ? await w.isFullscreen().catch(() => false) : false;
      if (disposed) return;
      if (full) root.dataset.fullscreen = "";
      else delete root.dataset.fullscreen;
    };
    void syncFullscreen();
    for (const event of ["tauri://resize", "tauri://focus", "tauri://resumed"] as const) {
      keep(listen(event, () => void syncFullscreen()));
    }

    return () => {
      disposed = true;
      for (const off of cleanups) off();
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("click", interceptLinks, true);
      document.removeEventListener("auxclick", interceptLinks, true);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      delete root.dataset.fullscreen;
      offSave();
    };
  }, []);

  const cancel = () => {
    setCloseBlocked(false);
    void invoke("app_close_cancelled");
  };

  return (
    <AppDialog
      open={closeBlocked}
      onClose={cancel}
      title="저장하지 못했습니다"
      width="sm"
      footer={
        <>
          <FuiButton density="dialog" appearance="secondary" onClick={cancel}>
            취소
          </FuiButton>
          <FuiButton density="dialog" appearance="subtle" onClick={() => void invoke("app_close_confirmed")}>
            저장하지 않고 종료
          </FuiButton>
          <FuiButton
            density="dialog"
            appearance="primary"
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                if (await exportRescueCopy()) await invoke("app_close_confirmed");
              } finally {
                setExporting(false);
              }
            }}
          >
            {exporting ? "내보내는 중…" : "내보내고 종료"}
          </FuiButton>
        </>
      }
    >
      <Body1>변경 내용을 이 기기에 저장하지 못했습니다. 지금 편집 중인 플랜을 JSON으로 내보낸 뒤 종료할까요?</Body1>
    </AppDialog>
  );
}
